import { UrlBuilderHelper } from './urlbuilder.helper';

interface IDomainRoutingConfig {
  enabled: boolean;
  baseDomain: string;
}

interface IEnvironmentBackendConfig {
  api: { baseUrl: string };
  auth: { baseUrl: string };
  websocketServer: { baseUrl: string };
  customDomainRouting?: IDomainRoutingConfig;
}

interface IBackendEndpoints {
  apiBaseUrl: string;
  authBaseUrl: string;
  websocketBaseUrl: string;
}

export class CustomDomainHelper {
  /**
   * Resolve API/Auth/WebSocket endpoints based on environment defaults and current hostname.
   *
   * When running on the platform's own base domain (e.g. `suite.newroom-connect.com`), the main root domain is use and no normalization occurs.
   *
   * When running on a project custom domain the backend always exposes its APIs as direct
   * subdomains of that custom domain:
   *
   * - Project custom domain : `mar20x-knowledge-hub.i.mercedes-benz.com`
   * - Data API              : `data-api.mar20x-knowledge-hub.i.mercedes-benz.com`
   * - Auth API              : `auth-api.mar20x-knowledge-hub.i.mercedes-benz.com`
   * - WebSocket             : `wss://data-api.mar20x-knowledge-hub.i.mercedes-benz.com`.
   *
   * @param environment
   * @param hostname
   *
   * @returns Resolved backend endpoints.
   */
  public static resolveBackendEndpoints(environment: IEnvironmentBackendConfig, hostname?: string): IBackendEndpoints {
    const defaults: IBackendEndpoints = {
      apiBaseUrl: environment.api.baseUrl,
      authBaseUrl: environment.auth.baseUrl,
      websocketBaseUrl: environment.websocketServer.baseUrl
    };

    const customDomainRouting = environment.customDomainRouting;
    const currentHostname = hostname || (typeof window !== 'undefined' ? window.location.hostname : '');

    // On the platform base domain (e.g. suite-dev.newroom-connect.com or any *.newroom-connect.com
    // Subdomain) skip custom domain handling entirely and return the env-configured URLs unchanged.
    if (!customDomainRouting || !CustomDomainHelper.isCustomDomainSupported(customDomainRouting, currentHostname)) {
      return defaults;
    }

    // On a project custom domain the full hostname IS the project domain. The backend mounts
    // Data-API and Auth-API as direct subdomains of it, so we use the hostname as-is.
    const projectCustomDomain = UrlBuilderHelper.normalizeHostname(currentHostname);
    const websocketProtocol = defaults.websocketBaseUrl.startsWith('ws://') ? 'ws' : 'wss';

    return {
      apiBaseUrl: `https://data-api.${projectCustomDomain}`,
      authBaseUrl: `https://auth-api.${projectCustomDomain}`,
      websocketBaseUrl: `${websocketProtocol}://data-api.${projectCustomDomain}`
    };
  }

  /**
   * Check whether the given hostname is a project custom domain rather than the platform's own
   * base domain. Returns `true` only when custom domain routing is enabled AND the hostname is
   * neither equal to the configured `baseDomain` nor a subdomain of it.
   *
   * @param customDomainRouting
   * @param currentHostname
   *
   * @returns `true` when the hostname belongs to a project custom domain.
   */
  private static isCustomDomainSupported(customDomainRouting: IDomainRoutingConfig, currentHostname: string): boolean {
    const normalizedHostname = UrlBuilderHelper.normalizeHostname(currentHostname);
    const normalizedBaseDomain = UrlBuilderHelper.normalizeHostname(customDomainRouting.baseDomain);

    const isPlatformDomain = normalizedHostname === normalizedBaseDomain || normalizedHostname.endsWith(`.${normalizedBaseDomain}`);

    return !isPlatformDomain && customDomainRouting.enabled;
  }
}
